import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AlertComponent } from '@components';
import { AcknowledgementOfDebtStatus, PaymentFrequency } from '@enums';
import { AcknowledgementOfDebt } from '@models';

@Component({
  selector: 'app-acknowledgement-of-debt',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatDatepickerModule,
    AlertComponent,
  ],
  templateUrl: './acknowledgement-of-debt.html',
  styleUrl: './acknowledgement-of-debt.scss',
})
export class AcknowledgementOfDebtComponent {
  public form: FormGroup;
  public selectedFile?: File;
  public uploadPreview: SafeResourceUrl | null = null;
  public dragActive = false;
  public error: string | null = null;
  public viewOnly = false;

  paymentFrequencies = [
    { value: PaymentFrequency.WEEKLY, label: 'Weekly' },
    { value: PaymentFrequency.MONTHLY, label: 'Monthly' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<AcknowledgementOfDebtComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer
  ) {
    this.form = this.formBuilder.group({
      expenseName: [
        { value: data?.expense?.name, disabled: true },
        Validators.required,
      ],
      status: [
        {
          value:
            data?.acknowledgementOfDebt?.status ??
            AcknowledgementOfDebtStatus.PENDING,
          disabled: true,
        },
        Validators.required,
      ],
      startDate: [
        data?.acknowledgementOfDebt?.startDate ?? null,
        Validators.required,
      ],
      lastDeductedDate: [
        {
          value: data?.acknowledgementOfDebt?.lastDeductedDate ?? null,
          disabled: true,
        },
      ],
      interestRate: [
        data?.acknowledgementOfDebt?.interestRate ?? 0,
        Validators.required,
      ],
      amount: [
        data?.acknowledgementOfDebt?.amount ?? 0,
        [Validators.required, Validators.min(1)],
      ],
      paymentFrequency: [
        data?.acknowledgementOfDebt?.paymentFrequency ??
          PaymentFrequency.WEEKLY,
        Validators.required,
      ],
      documentUrl: [data?.acknowledgementOfDebt?.documentUrl ?? null],
      deductionAmount: [
        data?.acknowledgementOfDebt?.deductionAmount ?? 0,
        [Validators.required, Validators.min(1)],
      ],
      balanceDue: [
        { value: data?.acknowledgementOfDebt?.balanceDue ?? 0, disabled: true },
      ],
      numberOfDeductions: [
        {
          value: data?.acknowledgementOfDebt?.numberOfDeductions ?? 0,
          disabled: true,
        },
      ],
    });

    this.selectedFile = data?.acknowledgementOfDebt?.documentUrl
      ? new File([], data?.acknowledgementOfDebt?.documentUrl)
      : undefined;

    if (this.selectedFile) {
      this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
        URL.createObjectURL(this.selectedFile)
      );
    }

    this.viewOnly = data?.viewOnly || false;
    if (this.viewOnly) {
      this.form.disable();
    }
  }

  public onFileSelected(event: any) {
    try {
      event.preventDefault();
      if (this.viewOnly) return;
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(file)
        );
        this.error = null;
      }
    } catch (e) {
      console.error('Error selecting file:', e);
      this.error = 'Failed to select file. Please try again.';
    }
  }

  public removeFile() {
    if (this.viewOnly) return;
    this.selectedFile = undefined;
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragActive = true;
  }

   public onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
  }

  public onFileDrop(event: DragEvent) {
    try {
      event.preventDefault();
      if (this.viewOnly) return;
      this.dragActive = false;
      if (event.dataTransfer?.files?.length) {
        this.selectedFile = event.dataTransfer.files[0];
        this.uploadPreview = this.sanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(this.selectedFile)
        );
        this.error = null;
      }
    } catch (e) {
      console.error('Error dropping file:', e);
      this.error = 'Failed to drop file. Please try again.';
    }
  }

  public close() {
    this.dialogRef.close();
  }

  public submit() {
    if (this.viewOnly) return;

    if (!this.data?.expense?.id || !this.form.valid || !this.selectedFile) {
      this.error = 'Please fill in all required fields and select a file.';
      return;
    }

    const acknowledgementOfDebt: AcknowledgementOfDebt = {
      ...this.form.value,
      expenseId: this.data?.expense?.id,
      startDate: this.form.value.startDate
        ? new Date(this.form.value.startDate)
        : null,
      lastDeductedDate: this.form.value.lastDeductedDate
        ? new Date(this.form.value.lastDeductedDate)
        : null,
    };

    this.dialogRef.close({
      acknowledgementOfDebt,
      file: this.selectedFile,
    });
  }
}
